import { Breakpoint, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { handleError } from '../../features/layout/layoutSlice';
import { TEntity, tEntityId } from '../../models/entity';
import { useEditPanelContext } from '../common/components/AdminDataGrid/EditPanelContext';
import { EditorModus } from '../common/components/EditDialog/EditDialog';
import { ErrorsType } from '../common/components/ErrorBoundary/ErrorsType';
import useDebounce from '../common/hooks/useDebounce';
import { RootState, store } from '../store';
import { ErrorObject } from './validateUtils';

type PromiseState<T = any> = {
    value: T,
    success: boolean,
};


export type PlainSearchOptions<T> = {
    plainSearch?: true;
    minimumSearchLength?: number;
    getOptions: (searchString: string) => Promise<T[]>;
    options?: T[];
};

export type StaticSearchOptions<T> = {
    plainSearch?: false;
    options: T[];
};

interface PlainSearchProps<T> {
    getOptions: (searchString: string) => Promise<T[]>;
    setOptions: (value: T[]) => void;
    cacheTime?: number;
    minimumSearchLength: number;
    open: boolean;
}

const promises = new WeakMap<Promise<any>, PromiseState>();

export function usePromise<T>(promise: Promise<any>): T {
    const state = promises.get(promise);
    if (state) {
        if (typeof state.success === 'boolean') {
            if (state.success) {
                return state.value;
            } else {
                throw state.value;
            }
        }
    } else {
        const result = {} as PromiseState<T>;
        promises.set(promise, result);
        promise.then(
            value => {
                result.value = value;
                result.success = true;
            },
            err => {
                result.value = err;
                result.success = false;
            });
    }
    throw promise;
}

export function useBase(): string {
    return new URL(document.baseURI).pathname;
}

/**
 * Use this hook to determine whether the user is in consultant mode or not.
 * <b>Important:</b> Remember to always verify the consultant setting on the
 * server before you commit any changes to the database!
 */
export function useIsConsultant(): boolean {
    return !!useSelector((state: RootState) => state.consultantSettings.consultantToken);
}

export function useRefWithCallback(onMount: any, onUnmount: any) {
    const nodeRef = useRef(null);

    const setRef = useCallback((node: any) => {
        if (nodeRef.current) {
            onUnmount(nodeRef.current);
        }

        nodeRef.current = node;

        if (nodeRef.current) {
            onMount(nodeRef.current);
        }
    }, [onMount, onUnmount]);

    return setRef;
}

export function useResizeObserver(element: React.RefObject<HTMLDivElement | undefined>, callback: ResizeObserverCallback, observeOptions?: ResizeObserverOptions | undefined) {
    const observer = useRef<ResizeObserver>();
    const current = element && element.current;

    useLayoutEffect(() => {
        if (observer && observer.current && current) {
            observer.current.unobserve(current);
        }
        const resizeObserverOrPolyfill = ResizeObserver;
        observer.current = new resizeObserverOrPolyfill(callback);
        if (observer.current && current) {
            observer.current.observe(current, observeOptions);
        }

        return () => {
            if (observer && observer.current && current) {
                observer.current.unobserve(current);
            }
        };
    }, [callback, current, observeOptions]);

}


export function useContainerBreakpointSize(divRef: React.RefObject<HTMLDivElement | undefined>, breakpoints?: Partial<{ [key in Breakpoint]: number }>) {
    const {breakpoints: defaultBreakpoints} = useTheme();
    const breakValues = defaultBreakpoints.values;
    const [containerSize, setContainerSize] = useState<Breakpoint>('sm');

    const set_breakpoint = useCallback(
        (width: number) => {
            if (!width) return;

            let size: Breakpoint = 'sm';
            if (width < (breakpoints?.sm ?? breakValues.sm)) size = 'xs';
            else if (width < (breakpoints?.md ?? breakValues.md)) size = 'sm';
            else if (width < (breakpoints?.lg ?? breakValues.lg)) size = 'md';
            else if (width < (breakpoints?.xl ?? breakValues.xl)) size = 'lg';
            else size = 'xl';

            setContainerSize(size);
        },
        [breakpoints, breakValues]
    );

    useResizeObserver(divRef, (entries) => {
        const div = entries[0];
        set_breakpoint(div.target.clientWidth);
    });

    return containerSize;
}

export function usePlainSearch<T>({
    cacheTime = 300_000,
    getOptions,
    setOptions,
    minimumSearchLength,
    open,
}: PlainSearchProps<T>) {
    const [loading, setLoading] = useState(false);
    const [searchString, setSearchString] = useState('');
    const searchStringDebonce = useDebounce(searchString, 400);
    const cachedGetOptions = useMemo(() => {
        const cache = new Map<string, { age: number, data: T[] }>();
        return async (q: string) => {
            const result = cache.get(q);
            if(result && result.age > Date.now() - cacheTime) {
                return result.data;
            }
        
            try {
                const options = (await getOptions(q)) ?? [];
                cache.set(q, { age: Date.now(), data: options });
                return options;
            } catch (ex) {
                return [];
            }
        };
    }, [cacheTime, getOptions]);

    useEffect(() => {
        setLoading(open && searchString.length >= minimumSearchLength);
    }, [minimumSearchLength, open, searchString]);

    useEffect(() => {
        let active = true;

        if (!open) {
            return;
        }

        (async () => {
            let data: T[] = [];
            if(searchStringDebonce.length >= minimumSearchLength) {
                data = await cachedGetOptions(searchStringDebonce);
            }

            if (active && data) {
                setOptions(data);
                setLoading(false);
            }
        })();

        return () => {
            active = false;
        };
    }, [cachedGetOptions, open, setOptions, searchStringDebonce, minimumSearchLength]);

    return {
        searchString,
        searchStringDebonce,
        setSearchString,
        loading,
        setLoading
    };
}

export interface validateFieldRequestProps<T extends TEntity> {
    validateService: (value: T) => Promise<boolean>;
    id: tEntityId;
    fieldValue: any;
    propery: keyof T;
    isValidate?: boolean;
    mode: EditorModus;
    value?: Partial<T>
}

export function useValidateReguest<T extends TEntity>({
                                                          validateService,
                                                          mode,
                                                          id,
                                                          fieldValue,
                                                          propery,
                                                          isValidate = true,
                                                          value: validationValue
                                                      }: validateFieldRequestProps<T>) {
    const {setErrors, validateField, setValid} = useEditPanelContext();
    useEffect(() => {
        if (isValidate) {
            const value = {id: id, [propery]: fieldValue} as T;
            validateService({id: id, [propery]: fieldValue, ...validationValue} as T)
              .catch(error => {
                if (error.statusCode === ErrorsType.InternalServerError)
                    store.dispatch(handleError(error));
                const newPropertyError = ErrorMap<T>(error, value);
                setErrors((prev) => {
                    var newError = {
                        ...prev,
                        ...newPropertyError
                    };
                    return newError;
                });
                setValid(false);
            });
        }
    }, [validateService, id, propery, fieldValue, mode, setErrors, validateField, validationValue, isValidate, setValid]);
}

export function ErrorMap<T extends object>(error: any, value: T): ErrorObject {
    if (!error && ('data' in error) && !error?.data)
        return {};
    const data = error.data;
    const properties = Object.keys(data);
    let mappedErrorObject: ErrorObject = {};
    properties.forEach(propertyName => {
        if (propertyName in value)
            mappedErrorObject[propertyName] = (data[propertyName] as string[])?.join('\n');
    });
    return mappedErrorObject;
}


export function useWindowSize() {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
        function updateSize() {
            setSize([window.innerWidth, window.innerHeight]);
        }
        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);
    return size;
}